import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, Bed, HandHeart, User, ArrowUpRight, MapPin } from "lucide-react";
import { useApp, api } from "../AppContext";
import { C, TRUST, UI, IMAGES } from "../content";
import { Reveal, MaskLine, SectionLabel, OmMark, ease } from "../components/Motion";
import Seo from "../components/Seo";

const ICONS = { clock: Clock, bed: Bed, "hand-heart": HandHeart, user: User };
const HERO_IMG = IMAGES.bhaktNiwasBuilding;

export default function Home() {
  const { lang, t } = useApp();
  const [news, setNews] = useState([]);
  const [festival, setFestival] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/festivals").then((r) => setFestival(r.data[0])).catch(() => {});
  }, []);

  const tagline = lang === "mr" ? C.hero.tagline_mr : C.hero.tagline_en;
  const L = (o) => o[lang] ?? o.en;

  return (
    <div>
      <Seo title="Shri Datta Kshetra Audumbar" description="Official website of Narayan Swami Math, Audumbar — a sacred Dattatreya pilgrimage centre on the Krishna river. Darshan timings, Bhakt Niwas booking, festivals, donations and directions." />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden bg-earth-900" data-testid="hero">
        <motion.img style={{ y: yImg }} src={HERO_IMG} alt="Shri Bhakt Niwas at Narayan Swami Math, Audumbar"
          className="absolute inset-0 w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-earth-900 via-earth-900/55 to-earth-900/30" />
        <div className="relative w-full max-w-[1400px] mx-auto px-4 lg:px-8 pb-16 lg:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 text-ochre-400 mb-6">
            <OmMark className="w-10 h-10" />
            <span className="text-sm uppercase tracking-[0.3em] text-shadow-lg">श्री गुरुदेव दत्त</span>
          </motion.div>
          <h1 className="font-serif-head text-white font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] text-shadow-lg">
            {tagline.map((line, i) => (
              <MaskLine key={i} delay={0.15 + i * 0.15}>{line}</MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease }}
            className="mt-7 max-w-2xl text-sand-100/90 text-lg lg:text-xl text-shadow-lg">
            {lang === "mr" ? C.hero.sub_mr : C.hero.sub_en}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1, ease }} className="mt-9 flex flex-wrap gap-4">
            <Link to="/bhakt-niwas" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-7 py-3.5 font-semibold transition-all hover:-translate-y-1">
              {UI.hero_book[lang]} <ArrowUpRight size={20} />
            </Link>
            <Link to="/audumbar" data-testid="hero-heritage-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10 transition-all">
              {lang === "mr" ? "वारसा जाणून घ्या" : "Discover the Heritage"}
            </Link>
            <Link to="/events" data-testid="hero-events-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10 transition-all">
              {lang === "mr" ? "अलीकडील उत्सव / कार्यक्रम" : "Recent Updates / Events"} <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* NEWS TICKER */}
      {news.length > 0 && (
        <div className="bg-deepred-700 text-white py-3 overflow-hidden relative z-10" data-testid="news-ticker">
          <div className="flex whitespace-nowrap animate-ticker">
            {news.concat(news).map((n, i) => (
              <span key={i} className="mx-8 text-sm font-medium inline-flex items-center gap-3">
                <OmMark className="w-4 h-4 text-ochre-400" /> {t(n, "title")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* QUICK FACTS STRIP */}
      <section className="bg-earth-900 text-sand-100 relative z-10" data-testid="facts-strip">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {C.facts.map((f, i) => (
            <Reveal key={i} delay={i * 0.08} className="px-6 py-8">
              <div className="font-serif-head text-2xl lg:text-3xl text-ochre-400">{L({ mr: f.mr, en: f.en })}</div>
              <div className="text-xs uppercase tracking-wider text-sand-100/60 mt-1">{L({ mr: f.sub_mr, en: f.sub_en })}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUICK LINKS BENTO */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-4 lg:px-8">
        <Reveal><SectionLabel>{lang === "mr" ? "त्वरित दुवे" : "Quick Links"}</SectionLabel></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-4">
          {C.quickLinks.map((q, i) => {
            const Icon = ICONS[q.icon];
            return (
              <Reveal key={q.path} delay={i * 0.08}>
                <Link to={q.path} data-testid={`quicklink-${q.path.slice(1)}`}
                  className="group block h-full rounded-2xl border border-earth-900/10 bg-white p-7 transition-all hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(59,34,19,0.12)]">
                  <div className="w-12 h-12 rounded-xl bg-saffron-500/10 text-saffron-600 flex items-center justify-center mb-6 group-hover:bg-saffron-500 group-hover:text-white transition-colors"><Icon size={24} /></div>
                  <div className="font-serif-head text-2xl text-earth-900">{L(q)}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-saffron-600 text-sm font-semibold">{UI.read_more[lang]} <ArrowUpRight size={16} /></div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FESTIVAL HIGHLIGHT */}
      {festival && (
        <section className="relative py-20 lg:py-28 bg-sand-100 overflow-hidden" data-testid="festival-highlight">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <SectionLabel>{lang === "mr" ? "आगामी उत्सव" : "Featured Festival"}</SectionLabel>
              <h2 className="font-serif-head text-4xl lg:text-6xl text-earth-900 font-bold leading-tight">{t(festival, "name")}</h2>
              <p className="mt-2 text-saffron-600 font-semibold text-lg">{t(festival, "month") || festival.date}</p>
              <p className="mt-5 text-earth-800/80 text-lg leading-relaxed">{t(festival, "desc")}</p>
              <Link to="/festivals" data-testid="festival-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-deepred-700 hover:bg-deepred-800 text-white px-6 py-3 font-semibold transition-all hover:-translate-y-1">
                {lang === "mr" ? "सर्व उत्सव" : "All Festivals"} <ArrowUpRight size={18} />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_70px_rgba(59,34,19,0.18)]">
                <img src="https://images.unsplash.com/photo-1637250750846-8247d2b1ea08?w=900" alt="Marigold puja offerings" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* MARQUEE */}
      <section className="py-16 bg-earth-900 overflow-hidden relative z-10" data-testid="marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array(2).fill(0).map((_, k) => (
            <div key={k} className="flex items-center">
              {["श्री गुरुदेव दत्त", "दिगंबरा दिगंबरा श्रीपाद वल्लभ दिगंबरा", "श्री नृसिंह सरस्वती", "औदुंबर क्षेत्र"].map((w, i) => (
                <span key={i} className="font-serif-head text-4xl lg:text-6xl text-ochre-400/90 mx-12 flex items-center gap-12">
                  {w} <OmMark className="w-8 h-8 text-saffron-500" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* HERITAGE TEASER */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal delay={0.1} className="order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-[0_30px_70px_rgba(59,34,19,0.15)]">
            <img src={IMAGES.courtyardShrine} alt="Samadhi Mandir courtyard at Audumbar" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <Reveal className="order-1 lg:order-2">
          <SectionLabel>{lang === "mr" ? "पवित्र वारसा" : "Sacred Heritage"}</SectionLabel>
          <h2 className="font-serif-head text-4xl lg:text-6xl text-earth-900 font-bold leading-tight">{t(C.audumbar, "title")}</h2>
          <p className="mt-6 text-earth-800/80 text-lg leading-relaxed">{t(C.audumbar, "intro")}</p>
          <div className="mt-6 flex items-center gap-2 text-earth-700"><MapPin size={18} className="text-saffron-600" />{lang === "mr" ? TRUST.address_mr : TRUST.address_en}</div>
          <Link to="/audumbar" data-testid="heritage-teaser-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-6 py-3 font-semibold transition-all hover:-translate-y-1">
            {UI.read_more[lang]} <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>

      {/* BHAKT NIWAS SHOWCASE */}
      <section className="relative overflow-hidden bg-earth-900" data-testid="niwas-showcase">
        <img src={IMAGES.bhaktNiwasBuilding} alt="भक्त निवास — pilgrim stay building at Audumbar" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-earth-900 via-earth-900/85 to-earth-900/40" />
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionLabel>{lang === "mr" ? "भक्तांसाठी निवास" : "Stay for Pilgrims"}</SectionLabel>
            <h2 className="font-serif-head text-white font-bold text-4xl lg:text-6xl leading-tight text-shadow-lg">
              {lang === "mr" ? "श्री भक्त निवास" : "Shri Bhakt Niwas"}
            </h2>
            <p className="mt-6 text-sand-100/90 text-lg leading-relaxed max-w-xl">
              {lang === "mr"
                ? "दत्त मंदिरापासून जवळच असलेल्या प्रशस्त भक्त निवासात वातानुकूलित व साध्या खोल्या, भोजन, वाहनतळ व सुरक्षिततेची सोय. आपल्या मुक्कामासाठी आजच विनंती नोंदवा."
                : "A spacious pilgrim stay close to the Datta Mandir — AC & non-AC rooms, meals, parking and round-the-clock security. Send your stay enquiry today."}
            </p>
            <Link to="/bhakt-niwas" data-testid="niwas-showcase-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-7 py-3.5 font-semibold transition-all hover:-translate-y-1">
              {UI.hero_book[lang]} <ArrowUpRight size={20} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl overflow-hidden border border-white/15 shadow-[0_30px_70px_rgba(0,0,0,0.4)]">
              <img src={IMAGES.bhaktNiwasBuilding} alt="Shri Bhakt Niwas building at Audumbar" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
